import { useId, useState, type ReactNode } from 'react'
import styles from './ui.module.css'

/**
 * A disclosure: a heading you press to reveal its body.
 *
 * A primitive, so it knows nothing about help content or any other subject
 * matter (docs/16 section 6.1). It is built on native `button` + `aria-expanded`
 * + `aria-controls` rather than `<details>`, because `<details>` cannot be
 * animated consistently and its summary marker fights every design system.
 *
 * The panel is unmounted when closed rather than hidden with CSS: a closed
 * section must not be reachable by keyboard, and must not be found by the
 * browser's own find-on-page, which would otherwise scroll to text nobody can see.
 *
 * Open state is held here until a caller passes `open`, at which point the
 * caller owns it. That is what lets a filtered list open the section it has just
 * reduced itself to, without this primitive knowing a filter exists.
 */
export function AccordionItem({
  title,
  caption,
  children,
  defaultOpen = false,
  open,
  onToggle,
  headingId,
}: {
  title: ReactNode
  /** A second line under the title, for context the heading alone cannot carry. */
  caption?: ReactNode
  children: ReactNode
  defaultOpen?: boolean
  /** Set to control the item from outside; `onToggle` then has to move it. */
  open?: boolean
  onToggle?: () => void
  /** Set when something outside needs to link to this item. */
  headingId?: string
}) {
  const [uncontrolled, setUncontrolled] = useState(defaultOpen)
  const isOpen = open ?? uncontrolled
  const generated = useId()
  const panelId = `${generated}-panel`
  const buttonId = headingId ?? `${generated}-button`

  return (
    <div className={styles.accordionItem}>
      <h3 className={styles.accordionHeading}>
        <button
          type="button"
          id={buttonId}
          className={styles.accordionTrigger}
          aria-expanded={isOpen}
          aria-controls={panelId}
          onClick={() => (onToggle ? onToggle() : setUncontrolled((wasOpen) => !wasOpen))}
        >
          <span className={styles.accordionTitle}>
            <span>{title}</span>
            {caption ? <span className={styles.accordionCaption}>{caption}</span> : null}
          </span>
          <Chevron open={isOpen} />
        </button>
      </h3>
      {isOpen ? (
        <div id={panelId} role="region" aria-labelledby={buttonId} className={styles.accordionPanel}>
          {children}
        </div>
      ) : null}
    </div>
  )
}

const Chevron = ({ open }: { open: boolean }) => (
  <svg
    className={open ? `${styles.accordionChevron} ${styles.accordionChevronOpen}` : styles.accordionChevron}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={2}
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="m6 9 6 6 6-6" />
  </svg>
)
